/**
 * Centralized Notification Sound Manager
 * Prevents multiple simultaneous sounds and manages sound preferences
 */
class NotificationSoundManager {
  static instance;
  static audioRef = null;
  static isPlaying = false;
  static lastSoundTime = 0;
  static SOUND_COOLDOWN = 2000; // 2 seconds between sounds
  static unlocked = false;

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!this.instance) {
      this.instance = new NotificationSoundManager();
    }
    return this.instance;
  }

  /**
   * Initialize the sound manager with audio file
   */
  static initialize(audioFile) {
    if (!this.audioRef) {
      this.audioRef = new Audio(audioFile);
      this.audioRef.volume = 0.5;
      this.audioRef.preload = "auto";

      // Browsers block audio with sound from playing until the page has
      // seen at least one user gesture — without this, the very first
      // real notification sound after page load fails silently (see the
      // swallowed catch in playSound()). Unlock on the first interaction
      // so real notifications work from then on.
      const unlock = () => this.unlockAudio();
      ["click", "keydown", "touchstart"].forEach((evt) =>
        document.addEventListener(evt, unlock, { once: true, passive: true }),
      );
    }
  }

  /**
   * Play-then-immediately-pause (muted, so there's no audible blip) to
   * satisfy the browser's "played after a user gesture" requirement.
   */
  static unlockAudio() {
    if (this.unlocked || !this.audioRef) {
      return;
    }
    this.unlocked = true;

    const wasMuted = this.audioRef.muted;
    this.audioRef.muted = true;
    const resetAfterUnlock = () => {
      this.audioRef.pause();
      this.audioRef.currentTime = 0;
      this.audioRef.muted = wasMuted;
    };

    const playPromise = this.audioRef.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise
        .then(resetAfterUnlock)
        .catch(() => {
          this.audioRef.muted = wasMuted;
          this.unlocked = false;
        });
    } else {
      resetAfterUnlock();
    }
  }

  /**
   * Play notification sound with proper checks
   */
  static playSound() {
    // Check if page is hidden (user not active)
    if (document.hidden) {
      return;
    }

    // Check cooldown period
    const now = Date.now();
    if (now - this.lastSoundTime < this.SOUND_COOLDOWN) {
      return;
    }

    // Check if already playing
    if (this.isPlaying) {
      return;
    }

    // Play the sound
    if (this.audioRef) {
      this.isPlaying = true;
      this.lastSoundTime = now;

      this.audioRef
        .play()
        .catch((error) => {
          // Most commonly NotAllowedError — the page hasn't seen a user
          // gesture yet, so unlockAudio() hasn't run. Logged (not thrown)
          // since a missed sound shouldn't break anything else.
          console.warn(
            "[NotificationSoundManager] Playback failed:",
            error?.name,
            error?.message,
          );
        })
        .finally(() => {
          // Reset playing flag after sound duration
          setTimeout(() => {
            this.isPlaying = false;
          }, 1000);
        });
    } else {
      console.warn("[NotificationSoundManager] Audio not initialized");
    }
  }

  /**
   * Reset the sound manager (useful for testing)
   */
  static reset() {
    this.isPlaying = false;
    this.lastSoundTime = 0;
  }
}

export default NotificationSoundManager;
